import type { JSX } from "solid-js";
import { Trash2 } from "lucide-solid";
import { msg } from "@lingui/core/macro";

import { useI18n } from "@/providers/i18n-provider";
import { useRowDeleteSwipe } from "@/lib/gestures/use-row-delete-swipe";

interface SwipeToDeleteRowProps {
  /** Commit handler: the row was swiped left past the threshold. */
  onDelete: () => void;
  /** Row content (kept as the keyboard/click path; controls stay interactive). */
  children: JSX.Element;
  /** Optional class for the outer list item / container. */
  class?: string;
  /** Optional inline style for the outer container (e.g. staggered animation-delay). */
  style?: JSX.CSSProperties;
  /** Optional test id forwarded to the outer container. */
  "data-testid"?: string;
  /**
   * Set when the row content is itself a link/button (e.g. a recipe card) so the
   * drag may begin on it; a tap still navigates (the gesture engages only past
   * the deadzone with a horizontal axis lock).
   */
  allowControlDrag?: boolean;
}

/**
 * Wraps a list row with premium swipe-to-delete: swipe left past the threshold
 * to remove. A "Delete" affordance is revealed beneath the sliding surface; the
 * gesture layers on top of the existing controls (the delete button / link
 * remain the keyboard/click path) via the shared recognizer (haptics,
 * axis-lock, reduced-motion safe through `transition: none` while dragging).
 */
export function SwipeToDeleteRow(props: SwipeToDeleteRowProps) {
  const { t } = useI18n();
  let surface: HTMLDivElement | undefined;

  const swipe = useRowDeleteSwipe({
    onDelete: () => props.onDelete(),
    getSurfaceRef: () => surface,
    allowControlDrag: () => props.allowControlDrag === true,
  });

  const transform = (): string => `translate3d(${swipe.tx()}px, 0, 0)`;
  const transition = (): string =>
    swipe.isAnimating() ? `transform ${swipe.CANCEL_ANIMATION_MS}ms ease-out` : "none";

  return (
    <div
      class={`relative overflow-hidden rounded-lg ${props.class ?? ""}`}
      style={props.style}
      data-testid={props["data-testid"]}
    >
      {/* Delete affordance revealed beneath the sliding surface (right edge). */}
      <div class="pointer-events-none absolute inset-0 flex items-center justify-end px-4">
        <span class="flex items-center gap-1 text-sm font-medium text-danger">
          <Trash2 class="h-4 w-4" />
          {t(msg`Delete`)}
        </span>
      </div>

      <div
        ref={surface}
        class="touch-pan-y"
        style={{ transform: transform(), transition: transition() }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
        on:click={{
          capture: true,
          handleEvent(e: MouseEvent) {
            // A swipe must not also fire the row's click/navigation. Swallow the
            // synthetic click that follows the gesture (capture phase, before it
            // reaches the inner link), then re-arm.
            if (swipe.getHasSwiped()) {
              e.preventDefault();
              e.stopPropagation();
              swipe.resetHasSwiped();
            }
          },
        }}
      >
        {props.children}
      </div>
    </div>
  );
}
